import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import styles from './product.module.scss';

// A section counts as current once its top passes this line (the bar height plus
// a little slack), so the tab switches when the heading reaches the bar.
const ACTIVE_LINE_PX = 96;

const useActiveSection = (ids, enabled) => {
  // A string key keeps the effect stable across renders that rebuild the same list.
  const idsKey = ids.join(',');
  const [activeId, setActiveId] = useState(ids[0]);

  useEffect(() => {
    if (!enabled) return undefined;

    const sectionIds = idsKey.split(',');
    let frame = null;
    const update = () => {
      frame = null;
      const passed = sectionIds.filter((id) => {
        const element = document.getElementById(id);
        return element && element.getBoundingClientRect().top <= ACTIVE_LINE_PX;
      });
      setActiveId(passed[passed.length - 1] ?? sectionIds[0]);
    };
    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [idsKey, enabled]);

  return activeId;
};

// Intercom's default launcher offset, and the phone bar height plus that offset
const INTERCOM_DEFAULT_PADDING_PX = 20;
const INTERCOM_ABOVE_BAR_PADDING_PX = 92;
const PHONE_QUERY = '(max-width: 991px)';

// The phone bar sits where the Intercom launcher is, so the launcher moves up while it shows
const useIntercomClearance = (visible) => {
  useEffect(() => {
    if (typeof window.Intercom !== 'function') return undefined;

    const lift = visible && window.matchMedia(PHONE_QUERY).matches;
    window.Intercom('update', {
      vertical_padding: lift ? INTERCOM_ABOVE_BAR_PADDING_PX : INTERCOM_DEFAULT_PADDING_PX,
    });
    return () => window.Intercom('update', { vertical_padding: INTERCOM_DEFAULT_PADDING_PX });
  }, [visible]);
};

/**
 * Bar shown once the buy button has scrolled out of view. Desktop: section tabs at
 * the top, selection, price and the buy action. Phones: fixed to the bottom without
 * tabs. The action is the same element the top section renders, so the bar has no
 * buy logic of its own.
 */
const StickyProductBar = ({ visible, tabs, summary, price, action }) => {
  const activeId = useActiveSection(
    tabs.map((tab) => tab.id),
    visible,
  );
  useIntercomClearance(visible);

  return (
    <div
      className={classNames(styles.stickyBar, { [styles.stickyVisible]: visible })}
      aria-hidden={!visible}
      inert={visible ? undefined : ''}
    >
      <div className={styles.stickyInner}>
        <nav className={styles.stickyTabs} aria-label="Product page sections">
          {tabs.map((tab) => (
            <a
              key={tab.id}
              href={`#${tab.id}`}
              className={classNames(styles.stickyTab, { [styles.active]: tab.id === activeId })}
              aria-current={tab.id === activeId ? 'location' : undefined}
            >
              {tab.label}
            </a>
          ))}
        </nav>
        <div className={styles.stickyBuy}>
          <div className={styles.stickySummary}>
            <span className={styles.stickySelection}>{summary}</span>
            <span className={styles.stickyPrice}>{price}</span>
          </div>
          {action}
        </div>
      </div>
    </div>
  );
};

StickyProductBar.propTypes = {
  visible: PropTypes.bool.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }))
    .isRequired,
  summary: PropTypes.string,
  price: PropTypes.node,
  action: PropTypes.node,
};

export default StickyProductBar;
